import type { CSSProperties } from "react";

type ColorValue = {
  hex?: string;
  cssRgb?: string;
};

type BrandColorRecord = {
  __typename?: "BrandColorRecord";
  name?: string;
  color?: ColorValue;
};

type ColorGradientRecord = {
  __typename?: "ColorGradientRecord";
  direction?: string;
  proportion?: number | string;
  mainColorDistribution?: string;
  firstColor?: BrandColorRecord;
  secondColor?: BrandColorRecord;
  color?: ColorValue;
};

export type BackgroundColorField = BrandColorRecord | ColorGradientRecord;

function brandColorHex(brand?: BrandColorRecord | null): string | undefined {
  return brand?.color?.hex ?? brand?.color?.cssRgb;
}

function isGradient(
  bg: BackgroundColorField | null | undefined
): bg is ColorGradientRecord {
  if (!bg || bg.__typename !== "ColorGradientRecord") return false;
  return Boolean(
    brandColorHex(bg.firstColor) && brandColorHex(bg.secondColor)
  );
}

/** CMS proportion is 10–90: where the blend completes along the axis (smooth, not a hard split). */
export function clampGradientProportion(proportion?: number | string): number {
  let value =
    typeof proportion === "string" ? Number.parseFloat(proportion) : proportion;

  if (typeof value !== "number" || Number.isNaN(value)) {
    return 50;
  }

  // CMS may store 0.7 instead of 70
  if (value > 0 && value <= 1) {
    value *= 100;
  }

  return Math.min(90, Math.max(10, Math.round(value)));
}

/** `direction` from CMS: `horizontal` | `vertical`. */
export function gradientAxis(direction?: string): "to right" | "to bottom" {
  const normalized = (direction ?? "horizontal").toLowerCase().trim();
  if (normalized === "vertical") {
    return "to bottom";
  }
  return "to right";
}

/** `full` (default): first color at 50%, second at 100%. `half`: first at floor(proportion / 2), second at proportion. */
export function resolveGradientFirstStop(
  proportion?: number | string,
  mainColorDistribution?: string
): number {
  const distribution = (mainColorDistribution ?? "full").toLowerCase().trim();

  if (distribution === "half") {
    return Math.floor(clampGradientProportion(proportion) / 2);
  }

  return 50;
}

export function resolveGradientSecondStop(
  proportion?: number | string,
  mainColorDistribution?: string
): number {
  const distribution = (mainColorDistribution ?? "full").toLowerCase().trim();

  if (distribution === "half") {
    return clampGradientProportion(proportion);
  }

  return 100;
}

/**
 * Two-stop gradient: first color holds through the first stop, then blends to second at the second stop.
 * e.g. `linear-gradient(to bottom, #CE0F69 50%, #FFADB1 100%)`
 */
export function buildGradientBackground(
  firstHex: string,
  secondHex: string,
  direction?: string,
  proportion?: number | string,
  mainColorDistribution?: string
): string {
  const axis = gradientAxis(direction);
  const firstStop = resolveGradientFirstStop(proportion, mainColorDistribution);
  const secondStop = resolveGradientSecondStop(proportion, mainColorDistribution);
  return `linear-gradient(${axis}, ${firstHex} ${firstStop}%, ${secondHex} ${secondStop}%)`;
}

type LegacyBgColorWrapper = {
  color?: ColorValue;
};

/** Resolves CMS `bgColor` (solid brand color or gradient) to inline styles. */
export function resolveBackgroundStyle(
  bgColor?:
    | BackgroundColorField
    | LegacyBgColorWrapper
    | null
): CSSProperties {
  if (!bgColor) return {};

  if (isGradient(bgColor)) {
    const first = brandColorHex(bgColor.firstColor);
    const second = brandColorHex(bgColor.secondColor);
    if (!first || !second) return {};

    return {
      background: buildGradientBackground(
        first,
        second,
        bgColor.direction,
        bgColor.proportion,
        bgColor.mainColorDistribution
      ),
    };
  }

  if ("color" in bgColor && bgColor.color?.hex) {
    return { backgroundColor: bgColor.color.hex };
  }

  const hex = bgColor.color?.hex ?? bgColor.color?.cssRgb;
  if (hex) {
    return { backgroundColor: hex };
  }

  return {};
}

/** Portfolio page: prefer `bgColorGr` (solid or gradient) over legacy `bgColor`. */
export function resolvePortfolioBackgroundStyle(
  bgColorGr?:
    | BackgroundColorField
    | LegacyBgColorWrapper
    | null,
  bgColor?:
    | BackgroundColorField
    | LegacyBgColorWrapper
    | null
): CSSProperties {
  return resolveBackgroundStyle(bgColorGr ?? bgColor);
}

/** Solid hex for CSS vars / buy-button contrast when background may be a gradient. */
export function resolvePortfolioBackgroundHex(
  bgColorGr?:
    | BackgroundColorField
    | LegacyBgColorWrapper
    | null,
  bgColor?:
    | BackgroundColorField
    | LegacyBgColorWrapper
    | null
): string | undefined {
  const preferred = bgColorGr ?? bgColor;
  if (!preferred) return undefined;

  if (isGradient(preferred)) {
    return (
      brandColorHex(preferred.firstColor) ??
      brandColorHex(preferred.secondColor)
    );
  }

  if ("color" in preferred && preferred.color?.hex) {
    return preferred.color.hex;
  }

  return preferred.color?.hex ?? preferred.color?.cssRgb;
}

export function isGradientBackgroundStyle(style: CSSProperties): boolean {
  const background = (style as CSSProperties & { background?: string }).background;

  return (
    typeof background === "string" && background.includes("linear-gradient")
  );
}
